import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Youtube, Play, ExternalLink } from 'lucide-react';

const youtubeVideos = [
    {
        id: 1,
        title: 'Hawayein The Band - Live Performance',
        description: 'Experience the magic of Sufi & Bollywood fusion live',
        videoId: 'w2DOETYh0ak',
        thumbnail: 'https://img.youtube.com/vi/w2DOETYh0ak/maxresdefault.jpg',
        type: 'video',
    },
    {
        id: 2,
        title: 'Yash Singh Thakur - Viral Performance',
        description: 'Watch the performance that took the internet by storm',
        videoId: 'mzDhCbqT9QY',
        thumbnail: 'https://img.youtube.com/vi/mzDhCbqT9QY/maxresdefault.jpg',
        type: 'short',
    },
    {
        id: 3,
        title: 'Hawayein Live Concert Highlights',
        description: 'Best moments from our electrifying concerts',
        videoId: 'FLb_EgsDOBs',
        thumbnail: 'https://img.youtube.com/vi/FLb_EgsDOBs/maxresdefault.jpg',
        type: 'video',
    },
];

export const YouTubeShowcase = () => {
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { once: true, margin: "-100px" });

    return (
        <section ref={containerRef} className="py-24 lg:py-32 relative overflow-hidden bg-card/30">
            {/* Background Effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-destructive/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-primary/10 rounded-full blur-[120px]" />
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-16"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                        className="inline-flex items-center gap-2 mb-4"
                    >
                        <Youtube className="w-6 h-6 text-destructive" />
                        <span className="text-sm font-body font-medium text-destructive uppercase tracking-wider">
                            Watch Us Perform
                        </span>
                    </motion.div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
                        Yash Singh Thakur Live
                    </h2>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-2xl mx-auto">
                        Experience the electrifying performances of <span className="text-primary font-semibold">Hawayein The Band</span> - 
                        India's Premier Sufi & Bollywood Live Band
                    </p>
                </motion.div>

                {/* Featured Video - Large */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    className="mb-10"
                >
                    <Card className="card-premium rounded-3xl overflow-hidden">
                        <CardContent className="p-0">
                            <div className="aspect-video w-full">
                                <iframe
                                    src="https://www.youtube.com/embed/w2DOETYh0ak?rel=0"
                                    title="Hawayein The Band - Live Performance"
                                    className="w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                            <div className="p-6 bg-gradient-to-r from-card to-card/80">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <Badge className="mb-2 bg-destructive/20 text-destructive border-none">
                                            <Youtube className="w-3 h-3 mr-1" /> Featured
                                        </Badge>
                                        <h3 className="font-heading font-bold text-xl text-foreground mb-2">
                                            Hawayein The Band - Live Performance
                                        </h3>
                                        <p className="text-muted-foreground text-sm font-body">
                                            Watch Yash Singh Thakur and Hawayein deliver an unforgettable Sufi & Bollywood experience
                                        </p>
                                    </div>
                                    <a
                                        href="https://www.youtube.com/watch?v=w2DOETYh0ak"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Button variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10">
                                            <ExternalLink className="w-4 h-4 mr-2" />
                                            YouTube
                                        </Button>
                                    </a>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Other Videos Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {youtubeVideos.slice(1).map((video, index) => (
                        <motion.div
                            key={video.id}
                            initial={{ opacity: 0, y: 30 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                        >
                            <Card className="card-premium rounded-2xl overflow-hidden group h-full">
                                <CardContent className="p-0 flex flex-col h-full">
                                    <div className="aspect-video w-full relative">
                                        <iframe
                                            src={`https://www.youtube.com/embed/${video.videoId}?rel=0`}
                                            title={video.title}
                                            className="w-full h-full"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    </div>
                                    <div className="p-5 flex-1 flex flex-col">
                                        <Badge className="w-fit mb-2 bg-muted/50 text-muted-foreground border-none text-xs">
                                            {video.type === 'short' ? '📱 YouTube Short' : '🎬 Full Video'}
                                        </Badge>
                                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                                            {video.title}
                                        </h3>
                                        <p className="text-muted-foreground text-sm font-body mb-4 flex-1">
                                            {video.description}
                                        </p>
                                        <a
                                            href={video.type === 'short' 
                                                ? `https://youtube.com/shorts/${video.videoId}` 
                                                : `https://www.youtube.com/watch?v=${video.videoId}`
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-auto"
                                        >
                                            <Button variant="ghost" className="p-0 h-auto text-destructive hover:text-destructive/80 font-heading font-semibold">
                                                Watch on YouTube
                                                <ExternalLink className="w-4 h-4 ml-2" />
                                            </Button>
                                        </a>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {/* Subscribe CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="text-center mt-12"
                >
                    <a
                        href="https://www.youtube.com/@hawayeintheband"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Button className="bg-destructive hover:bg-destructive/90 text-white font-heading font-semibold px-8 py-6 rounded-xl">
                            <Youtube className="w-5 h-5 mr-2" />
                            Subscribe to Our Channel
                        </Button>
                    </a>
                </motion.div>
            </div>
        </section>
    );
};
